import {
  useEffect,
  useCallback,
  useState,
  useLayoutEffect,
  type MouseEvent,
  type TouchEvent,
} from "react";
import { useCalibration } from "../../contexts/CalibrationContext";
import { useShedConfig } from "../../contexts/ShedConfigContext";

export default function ShedPositioner() {
  const {
    isSurveyLoaded,
    surveyType,
    scaleFactor,
    isShedPlaced,
    isShedLocked,
    shedPosition,
    shedRotation,
    shedOverlayRef,
    surveyContainerRef,
    imageViewerRef,
    pdfViewerRef,
    setShedPosition,
    setShedRotation,
    lockShed,
  } = useCalibration();

  const { dimensions } = useShedConfig();

  const [isDragging, setIsDragging] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState<{
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    containerRect: DOMRect;
  } | null>(null);
  const [rotateCenter, setRotateCenter] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [rotateStartAngle, setRotateStartAngle] = useState(0);
  const [lastRotation, setLastRotation] = useState(0); // Track last rotation for continuous movement
  const [surveyDims, setSurveyDims] = useState<{
    width: number;
    height: number;
    displayWidth: number;
    displayHeight: number;
  } | null>(null);

  // Rotation sensitivity - lower values make rotation slower and more controlled
  // 0.5 = 50% of actual mouse/finger movement (adjust between 0.1-1.0)
  // Supports full 360-degree continuous rotation
  const ROTATION_SENSITIVITY = 0.8;

  const getSurveyDimensions = useCallback(() => {
    if (surveyType === "image" && imageViewerRef.current) {
      const img = imageViewerRef.current;
      if (img.complete && img.naturalWidth > 0) {
        return {
          width: img.naturalWidth,
          height: img.naturalHeight,
          displayWidth: img.offsetWidth,
          displayHeight: img.offsetHeight,
        };
      }
    } else if (surveyType === "pdf" && pdfViewerRef.current) {
      const canvas = pdfViewerRef.current;
      if (canvas.width > 0) {
        return {
          width: canvas.width,
          height: canvas.height,
          displayWidth: canvas.offsetWidth,
          displayHeight: canvas.offsetHeight,
        };
      }
    }
    return null;
  }, [surveyType, imageViewerRef, pdfViewerRef]);

  // Calculate shed dimensions with fallback values and minimum size for better interaction
  const calculateShedDimensions = useCallback(() => {
    const baseWidthPx = scaleFactor
      ? (Number(dimensions.width) || 10) / scaleFactor
      : 100;
    const baseLengthPx = scaleFactor
      ? (Number(dimensions.length) || 12) / scaleFactor
      : 100;

    // For images, apply minimum size to ensure usability on high-resolution images
    if (surveyType === "image" && surveyDims) {
      const scaleX = surveyDims.displayWidth / surveyDims.width;
      const scaleY = surveyDims.displayHeight / surveyDims.height;

      // Calculate display size with current scaling
      const currentDisplayWidth = baseWidthPx * scaleX;
      const currentDisplayHeight = baseLengthPx * scaleY;

      // Responsive minimum display size (25px for desktop, 15px for mobile)
      const isMobile = window.innerWidth <= 768;
      const minDisplaySize = isMobile ? 15 : 25;

      // If the shed would be too small in display, adjust the base size
      if (
        currentDisplayWidth < minDisplaySize ||
        currentDisplayHeight < minDisplaySize
      ) {
        const widthMultiplier =
          currentDisplayWidth < minDisplaySize
            ? minDisplaySize / currentDisplayWidth
            : 1;
        const heightMultiplier =
          currentDisplayHeight < minDisplaySize
            ? minDisplaySize / currentDisplayHeight
            : 1;
        const multiplier = Math.max(widthMultiplier, heightMultiplier);

        return {
          width: baseWidthPx * multiplier,
          length: baseLengthPx * multiplier,
        };
      }
    }

    return {
      width: baseWidthPx,
      length: baseLengthPx,
    };
  }, [scaleFactor, dimensions, surveyType, surveyDims]);

  const { width: shedWidthPx, length: shedLengthPx } =
    calculateShedDimensions();

  useEffect(() => {
    if (isShedPlaced && shedPosition.x === 0 && shedPosition.y === 0) {
      const surveyDims = getSurveyDimensions();
      if (surveyDims) {
        setShedPosition({
          x: surveyDims.width / 2,
          y: surveyDims.height / 2,
        });
      }
    }
  }, [isShedPlaced, shedPosition, setShedPosition, getSurveyDimensions]);

  // Update survey dimensions using ResizeObserver for responsive updates
  useLayoutEffect(() => {
    const updateDimensions = () => {
      const dims = getSurveyDimensions();
      setSurveyDims(dims);
    };

    // Initial update
    updateDimensions();

    // Watch for resize changes
    const element =
      surveyType === "image" ? imageViewerRef.current : pdfViewerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    getSurveyDimensions,
    isSurveyLoaded,
    surveyType,
    imageViewerRef,
    pdfViewerRef,
  ]);

  // Helper to get coordinates from mouse or touch events
  const getEventCoordinates = useCallback(
    (
      e:
        | MouseEvent
        | TouchEvent
        | globalThis.MouseEvent
        | globalThis.TouchEvent,
    ) => {
      if ("touches" in e && e.touches && e.touches.length > 0) {
        return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
      }
      return {
        clientX: (e as globalThis.MouseEvent).clientX,
        clientY: (e as globalThis.MouseEvent).clientY,
      };
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (isShedLocked) return;
      e.preventDefault();
      e.stopPropagation();

      const coords = getEventCoordinates(e);
      const surveyDims = getSurveyDimensions();
      if (!surveyDims) return;

      const container = surveyContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      const scaleX = surveyDims.width / surveyDims.displayWidth;
      const scaleY = surveyDims.height / surveyDims.displayHeight;

      const containerX = coords.clientX - rect.left;
      const containerY = coords.clientY - rect.top;

      const actualX = containerX * scaleX;
      const actualY = containerY * scaleY;

      setIsDragging(true);
      setDragStart({
        x: actualX - shedPosition.x,
        y: actualY - shedPosition.y,
        scaleX,
        scaleY,
        containerRect: rect,
      });
    },
    [
      isShedLocked,
      shedPosition,
      getSurveyDimensions,
      surveyContainerRef,
      getEventCoordinates,
    ],
  );

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (isShedLocked) return;
      e.preventDefault();
      e.stopPropagation();

      const coords = getEventCoordinates(e);
      const surveyDims = getSurveyDimensions();
      if (!surveyDims) return;

      const container = surveyContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      const scaleX = surveyDims.width / surveyDims.displayWidth;
      const scaleY = surveyDims.height / surveyDims.displayHeight;

      const containerX = coords.clientX - rect.left;
      const containerY = coords.clientY - rect.top;

      const actualX = containerX * scaleX;
      const actualY = containerY * scaleY;

      setIsDragging(true);
      setDragStart({
        x: actualX - shedPosition.x,
        y: actualY - shedPosition.y,
        scaleX,
        scaleY,
        containerRect: rect,
      });
    },
    [
      isShedLocked,
      shedPosition,
      getSurveyDimensions,
      surveyContainerRef,
      getEventCoordinates,
    ],
  );

  const handleRotateStart = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (isShedLocked) return;
      e.preventDefault();
      e.stopPropagation();
      setIsRotating(true);

      const coords = getEventCoordinates(e);
      if (shedOverlayRef.current) {
        const rect = shedOverlayRef.current.getBoundingClientRect();
        const center = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        setRotateCenter(center);
        setRotateStartAngle(
          Math.atan2(coords.clientY - center.y, coords.clientX - center.x),
        );
        // Store the current rotation as the starting point for this gesture
        setLastRotation(shedRotation);
      }
    },
    [isShedLocked, shedOverlayRef, getEventCoordinates, shedRotation],
  );

  useEffect(() => {
    const handlePointerMove = (
      e: globalThis.MouseEvent | globalThis.TouchEvent,
    ) => {
      if (isDragging && dragStart) {
        const coords = getEventCoordinates(e);
        const containerX = coords.clientX - dragStart.containerRect.left;
        const containerY = coords.clientY - dragStart.containerRect.top;

        const actualX = containerX * dragStart.scaleX;
        const actualY = containerY * dragStart.scaleY;

        setShedPosition({
          x: actualX - dragStart.x,
          y: actualY - dragStart.y,
        });
      }

      if (isRotating && rotateCenter) {
        const coords = getEventCoordinates(e);
        const currentAngle = Math.atan2(
          coords.clientY - rotateCenter.y,
          coords.clientX - rotateCenter.x,
        );

        // Calculate the raw angle difference from the start
        let deltaAngle = currentAngle - rotateStartAngle;

        // Handle angle wrap-around (from -π to π)
        if (deltaAngle > Math.PI) {
          deltaAngle -= 2 * Math.PI;
        } else if (deltaAngle < -Math.PI) {
          deltaAngle += 2 * Math.PI;
        }

        // Apply rotation sensitivity and convert to degrees
        const deltaRotation =
          ((deltaAngle * 180) / Math.PI) * ROTATION_SENSITIVITY;

        // Calculate new rotation based on the initial rotation plus the delta
        let newRotation = lastRotation + deltaRotation;

        // Normalize to 0-360 degrees to allow full 360-degree rotation
        while (newRotation < 0) newRotation += 360;
        while (newRotation >= 360) newRotation -= 360;

        setShedRotation(newRotation);
      }
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      setIsRotating(false);
      setDragStart(null);
      setRotateCenter(null);
      setRotateStartAngle(0);
      // Keep the current rotation as the base for next rotation
      setLastRotation(shedRotation);
    };

    if (isDragging || isRotating) {
      // Add both mouse and touch events
      document.addEventListener("mousemove", handlePointerMove);
      document.addEventListener("mouseup", handlePointerUp);
      document.addEventListener("touchmove", handlePointerMove, {
        passive: false,
      });
      document.addEventListener("touchend", handlePointerUp);
    }

    return () => {
      document.removeEventListener("mousemove", handlePointerMove);
      document.removeEventListener("mouseup", handlePointerUp);
      document.removeEventListener("touchmove", handlePointerMove);
      document.removeEventListener("touchend", handlePointerUp);
    };
  }, [
    isDragging,
    isRotating,
    dragStart,
    rotateCenter,
    rotateStartAngle,
    lastRotation,
    shedRotation,
    setShedPosition,
    setShedRotation,
    setLastRotation,
    getEventCoordinates,
  ]);

  if (!isSurveyLoaded || !isShedPlaced) {
    return null;
  }

  if (!surveyDims) return null;

  const scaleX = surveyDims.displayWidth / surveyDims.width;
  const scaleY = surveyDims.displayHeight / surveyDims.height;

  const displayX = shedPosition.x * scaleX;
  const displayY = shedPosition.y * scaleY;
  const displayWidth = shedWidthPx * scaleX;
  const displayHeight = shedLengthPx * scaleY;

  return (
    <div
      ref={shedOverlayRef}
      className={`absolute cursor-move ${isShedLocked ? "pointer-events-none" : ""}`}
      style={{
        left: displayX,
        top: displayY,
        width: displayWidth,
        height: displayHeight,
        transform: `translate(-50%, -50%) rotate(${shedRotation}deg)`,
        border: "3px dashed #2f5241",
        backgroundColor: "rgba(47, 82, 65, 0.2)",
        zIndex: 20,
        touchAction: "none", // Prevent browser scrolling on touch
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {!isShedLocked && (
        <div
          className="absolute cursor-grab"
          style={{
            top: "-20px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#DDA248",
            border: "2px solid #fff",
            zIndex: 25,
            touchAction: "none",
          }}
          onMouseDown={handleRotateStart}
          onTouchStart={handleRotateStart}
        />
      )}

      {!isShedLocked && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            lockShed();
          }}
          className="absolute bg-purple-600 text-white text-xs px-2 py-1 rounded hover:bg-purple-700 "
          style={{
            bottom: "-30px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 25,
          }}
        >
          Lock
        </button>
      )}

      <div
        className="absolute text-lg font-bold text-[#2f5241]"
        style={{
          bottom: "2px",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        +
      </div>
    </div>
  );
}
